<template>
  <Teleport to="body">
    <Transition name="form-floating-actions">
      <div
        v-if="formFloatingActions.visible"
        class="form-floating-actions"
        role="region"
        :aria-label="t('forms.formActions')"
      >
        <div class="form-floating-actions__buttons">
          <BaseButton
            v-if="formFloatingActions.onCancel"
            variant="secondary"
            type="button"
            @click="triggerFormFloatingCancel"
          >
            {{ cancelLabel }}
          </BaseButton>
          <span
            v-if="formFloatingActions.onSubmit"
            class="form-floating-actions__submit-wrap"
            :class="{
              'form-floating-actions__submit-wrap--disabled': !canSubmit && !isLoading,
              'form-floating-actions__submit-wrap--has-tooltip': !canSubmit && !isLoading,
            }"
            :data-tooltip="!canSubmit && !isLoading ? submitDisabledHint : undefined"
            :tabindex="!canSubmit && !isLoading ? 0 : undefined"
          >
            <BaseButton
              type="button"
              variant="primary"
              :loading="isLoading"
              :disabled="!canSubmit"
              :aria-label="!canSubmit && !isLoading ? submitDisabledHint : submitLabel"
              icon="fa-floppy-disk"
              @click="triggerFormFloatingSubmit"
            >
              {{ submitLabel }}
            </BaseButton>
          </span>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/components/shared/BaseButton.vue'
import {
  formFloatingActions,
  triggerFormFloatingCancel,
  triggerFormFloatingSubmit,
} from '@/utils/formFloatingActions'

const { t } = useI18n()

const submitLabel = computed(
  () => formFloatingActions.getSubmitLabel?.() || formFloatingActions.submitLabel || t('forms.save'),
)

const cancelLabel = computed(
  () => formFloatingActions.cancelLabel || t('forms.cancel'),
)

const isLoading = computed(
  () => formFloatingActions.loading || Boolean(formFloatingActions.getLoading?.()),
)

const canSubmit = computed(() => formFloatingActions.canSubmit)

const submitDisabledHint = computed(() => t('forms.submitDisabledHint'))
</script>

<style scoped>
.form-floating-actions {
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 1200;
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1.25rem calc(0.75rem + env(safe-area-inset-bottom, 0px));
  pointer-events: none;
}

.form-floating-actions__buttons {
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.625rem;
  flex-shrink: 0;
}

.form-floating-actions__buttons :deep(.base-btn) {
  border: none;
}

.form-floating-actions__buttons :deep(.base-btn--primary:not(:disabled):not(.base-btn--loading)) {
  transition: background 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.form-floating-actions__buttons :deep(.base-btn--secondary:not(:disabled):not(.base-btn--loading)) {
  transition: background-color 0.15s ease;
}

@media (hover: hover) {
  .form-floating-actions__buttons :deep(.base-btn--primary:not(:disabled):not(.base-btn--loading):hover),
  .form-floating-actions__buttons :deep(.base-btn--primary:not(:disabled):not(.base-btn--loading):focus-visible) {
    filter: brightness(0.92);
    box-shadow: 0 2px 8px rgba(216, 103, 12, 0.25);
  }

  .form-floating-actions__buttons :deep(.base-btn--secondary:not(:disabled):not(.base-btn--loading):hover),
  .form-floating-actions__buttons :deep(.base-btn--secondary:not(:disabled):not(.base-btn--loading):focus-visible) {
    background-color: #e5e7eb;
  }
}

.form-floating-actions__submit-wrap {
  display: inline-flex;
  position: relative;
}

.form-floating-actions__submit-wrap--disabled {
  cursor: not-allowed;
}

.form-floating-actions__submit-wrap--disabled :deep(.base-btn) {
  pointer-events: none;
}

.form-floating-actions__submit-wrap--has-tooltip::after {
  content: attr(data-tooltip);
  position: absolute;
  right: 0;
  bottom: calc(100% + 10px);
  transform: translateY(4px);
  width: max-content;
  max-width: 16rem;
  padding: 0.45rem 0.65rem;
  border-radius: 0.5rem;
  background: #111827;
  color: #fff;
  font-size: 0.75rem;
  line-height: 1.35;
  text-align: center;
  white-space: normal;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 1300;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.18);
}

.form-floating-actions__submit-wrap--has-tooltip::before {
  content: '';
  position: absolute;
  right: 1.25rem;
  bottom: calc(100% + 4px);
  border: 6px solid transparent;
  border-top-color: #111827;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 1300;
}

.form-floating-actions__submit-wrap--has-tooltip:hover::after,
.form-floating-actions__submit-wrap--has-tooltip:focus-visible::after,
.form-floating-actions__submit-wrap--has-tooltip:hover::before,
.form-floating-actions__submit-wrap--has-tooltip:focus-visible::before {
  opacity: 1;
  transform: translateY(0);
}

.form-floating-actions-enter-active,
.form-floating-actions-leave-active {
  transition:
    opacity 0.32s ease,
    transform 0.38s cubic-bezier(0.22, 1, 0.36, 1);
}

.form-floating-actions-enter-from,
.form-floating-actions-leave-to {
  opacity: 0;
  transform: translateY(110%);
}
</style>
